import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function HowToPay() {
  const paymentMethods = [
    {
      title: 'Online Bank Transfer',
      icon: 'ri-smartphone-line',
      color: 'bg-blue-500',
      description: 'Transfer funds directly using your bank\'s online or mobile banking platform',
      steps: [
        'Log into your online banking app',
        'Select "Transfer / Payment"',
        'Enter MGMT University banking account details',
        'Enter your Student ID Number in the reference field',
        'Submit payment and screenshot your confirmation receipt'
      ]
    },
    {
      title: 'In-Branch Bank Deposit',
      icon: 'ri-building-line',
      color: 'bg-green-500',
      description: 'Visit any branch of the listed banks and deposit funds directly',
      steps: [
        'Visit any Republic Bank or CIBC FirstCaribbean branch',
        'Bring cash or cheque for deposit',
        'Provide the University account details to the teller',
        'Include your Student ID Number as reference',
        'Request a printed receipt from the teller'
      ]
    },
    {
      title: 'Debit/Credit Card Payment',
      icon: 'ri-bank-card-line',
      color: 'bg-purple-500',
      description: 'Pay securely through the University\'s online Student Portal',
      steps: [
        'Log into the University Student Portal',
        'Navigate to "Fee Payment" section',
        'Select your outstanding fees',
        'Enter your card details securely',
        'Complete payment and save confirmation'
      ]
    },
    {
      title: 'International Wire Transfer',
      icon: 'ri-global-line',
      color: 'bg-orange-500',
      description: 'For students residing overseas using SWIFT/BIC codes',
      steps: [
        'Contact your international bank',
        'Request a wire transfer to Barbados',
        'Provide University SWIFT/BIC code',
        'Include Student ID in transfer details',
        'Keep wire transfer receipt for records'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/fda573c0c4c77d9dfc61c22517570c94.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-blue-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">How to Pay University Fees</h1>
          <p className="text-xl max-w-4xl leading-relaxed">
            MGMT University offers multiple convenient channels for students to pay their tuition and administrative fees. Please choose a payment method that suits your location and banking access. It is recommended to always place your Student ID Number as the payment reference to ensure fast matching and confirmation by Student Accounts.
          </p>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Methods</h2>
            <p className="text-lg text-gray-600">Choose the payment option that works best for you</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {paymentMethods.map((method, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className={`${method.color} p-6 text-white`}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className={`${method.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="text-2xl font-bold">{method.title}</h3>
                  </div>
                  <p className="mt-4 text-white/90">{method.description}</p>
                </div>
                
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Steps:</h4>
                  <ol className="space-y-3">
                    {method.steps.map((step, stepIndex) => (
                      <li key={stepIndex} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-semibold text-gray-600">{stepIndex + 1}</span>
                        </div>
                        <span className="text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Tips */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Important Payment Tips</h2>
            <p className="text-lg text-gray-600">Follow these guidelines for successful payments</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Always Include Student ID</h3>
              <p className="text-gray-600">Your Student ID Number must be included in every payment reference to ensure proper account matching.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Pay Early</h3>
              <p className="text-gray-600">Submit payments at least 7 days before deadlines to allow for processing time and avoid late fees.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Keep Records</h3>
              <p className="text-gray-600">Always save payment confirmations, receipts, and screenshots as proof of payment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Reference Format */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-information-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Reference Format</h3>
              <p className="text-gray-600">Use this exact format when making payments</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-blue-200">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Example Reference:</p>
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <code className="text-lg font-mono text-gray-900">
                    "ID#: 821012345 – Tuition Payment Semester 1"
                  </code>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">ID#:</div>
                    <div className="text-gray-600">Prefix identifier</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">821012345</div>
                    <div className="text-gray-600">Your Student ID</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">Payment Description</div>
                    <div className="text-gray-600">What you\'re paying for</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Need More Information?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="/bank-details"
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group text-center"
            >
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-bank-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Bank Account Details</h3>
              <p className="text-gray-600 text-sm">Get the account information you need</p>
            </a>
            
            <a
              href="/deadlines"
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group text-center"
            >
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-calendar-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Deadlines</h3>
              <p className="text-gray-600 text-sm">Check important payment dates</p>
            </a>
            
            <a
              href="/contact"
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group text-center"
            >
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-customer-service-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Support</h3>
              <p className="text-gray-600 text-sm">Get help with your payments</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}